import arrayUnique from 'array-unique';
import React from 'react';

import { type SelectColumnDefinition, type SelectColumnOption, table } from '@gitbook/doc-core';
import {
    createPalette,
    matchCommands,
    type PaletteCommandDef,
    PaletteCommandType,
    type PaletteDef,
    type PaletteGroupDef,
    PalettePositionWidth,
    Tag,
    usePaletteHandle,
} from '@gitbook/react-uikit';
import { generateKey } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { confirm, input } from '@gitbook/spine-react';
import type { DeepReadonly } from '@gitbook/util-immutable';

import { InsertValueButton } from './buttons/InsertValueButton';
import type {
    RecordValueRenderProps,
    TableDefinitionCommandsParams,
    TableDefinitionRenderSpec,
} from './types';

/**
 * Creates a reused palette for selecting or adding options for a Select column.
 */
export function createSelectOptionsPalette({
    definition,
    value,
    nodeKey,
    editor,
    onValueChange,
}: {
    definition: DeepReadonly<SelectColumnDefinition>;
    value: string[];
    nodeKey?: string;
    editor?: EditorContainer;
    onValueChange: (newValue: string[]) => Promise<void> | void;
}): PaletteDef {
    return createPalette({
        search: {
            placeholder: definition.multiple ? 'Select or add options' : 'Select or add option',
        },
        position: { width: PalettePositionWidth.SMALL },
        groups: [
            {
                key: 'select.options.choose',
                commands: (palette) => {
                    const query = palette.query.text.trim();

                    return searchToAddOptionCommands({
                        definition,
                        setDefinition: (newDefinition) => {
                            if (editor && nodeKey) {
                                editor.change((change) => {
                                    table.setColumnDefinition(
                                        change,
                                        nodeKey,
                                        definition.id,
                                        newDefinition
                                    );
                                });
                            }
                            return;
                        },
                        query,
                        onNewOptionAdded: (newOption) => {
                            // Auto-select the option if the user typed a new label
                            const newValue = definition.multiple
                                ? arrayUnique([...value, newOption.value])
                                : [newOption.value];
                            onValueChange(newValue);
                        },
                        optionCommandBuilder: (option) => {
                            const isSelected = value.includes(option.value);
                            return {
                                key: option.value,
                                title: option.label,
                                ...(definition.multiple
                                    ? {
                                          type: PaletteCommandType.CHECKBOX,
                                          selected: isSelected,
                                      }
                                    : {
                                          type: PaletteCommandType.RADIO,
                                          selected: isSelected,
                                      }),
                                trigger: async () => {
                                    if (isSelected) {
                                        await onValueChange(
                                            value.filter((v) => v !== option.value)
                                        );
                                    } else {
                                        await onValueChange(
                                            arrayUnique(
                                                definition.multiple
                                                    ? [...value, option.value]
                                                    : [option.value]
                                            )
                                        );
                                    }
                                },
                            };
                        },
                    });
                },
            },
        ],
    });
}

export const selectDefinitionSpec: TableDefinitionRenderSpec<string[], SelectColumnDefinition> = {
    title: 'Select',
    description: 'Select allows you to select an option from predefined options in a dropdown.',
    icon: Icon.ColumnTypeSelect,
    ValueComponent: RecordSelectValue,
    commands: selectColumnCommands,
    onKeyDown: (key, value, setValue) => {
        switch (key) {
            case 'backspace':
                if (value.length > 0) {
                    setValue([]);
                }
                break;
        }
    },
};

/**
 * Column-level settings palette for a Select column.
 * Includes:
 * - Toggling "Allow multiple"
 * - Manage options (opens child palette to add/edit/delete options)
 */
function selectColumnCommands(
    params: TableDefinitionCommandsParams<SelectColumnDefinition>
): PaletteGroupDef[] {
    const { definition, setDefinition } = params;

    return [
        {
            key: 'select',
            title: 'Select settings',
            commands: [
                {
                    key: 'select.allow_multiple',
                    title: 'Allow multiple',
                    icon: Icon.OptionAdd,
                    type: PaletteCommandType.SWITCH,
                    enabled: definition.multiple,
                    trigger: () => {
                        const [palette] = setDefinition({
                            ...definition,
                            multiple: !definition.multiple,
                        });
                        return palette;
                    },
                },
                {
                    key: 'select.options',
                    title: 'Manage options',
                    icon: Icon.Options02,
                    descendant: () => manageSelectOptionsPalette(params),
                },
            ],
        },
    ];
}

/**
 * Nested palette for managing the options on the column-level.
 * Includes:
 * - Add a new option
 * - Edit or delete existing options
 */
function manageSelectOptionsPalette(
    params: TableDefinitionCommandsParams<SelectColumnDefinition>
): PaletteDef {
    const { definition, setDefinition } = params;
    const { options } = definition;

    return createPalette({
        search: {
            placeholder: 'Search, or add a new entry',
        },
        groups: [
            {
                key: 'select.options.manage',
                commands: (palette) => {
                    const query = palette.query.text.trim();

                    return searchToAddOptionCommands({
                        definition,
                        setDefinition: (newDefinition) => {
                            // We want to chain the setDefinition so the palette updates properly
                            const [, newCommandParams] = setDefinition(newDefinition);
                            return manageSelectOptionsPalette(newCommandParams);
                        },
                        query,
                        onNewOptionAdded: undefined,
                        // For "manage" mode, we show a descendant with "Edit" / "Delete".
                        optionCommandBuilder: (option, index) => ({
                            key: option.value,
                            title: option.label,
                            descendant: () => {
                                return createPalette({
                                    groups: [
                                        {
                                            key: 'select.options.manageOption',
                                            commands: [
                                                {
                                                    key: 'editOption',
                                                    title: 'Rename',
                                                    icon: Icon.Edit,
                                                    trigger: () => {
                                                        void input(
                                                            {
                                                                title: 'Rename option',
                                                                description:
                                                                    'What do you want to rename it to?',
                                                                confirmLabel: 'Save',
                                                                onConfirm: async (
                                                                    newLabel: string
                                                                ) => {
                                                                    const newOptions = [...options];
                                                                    newOptions[index] = {
                                                                        ...option,
                                                                        label: newLabel,
                                                                    };
                                                                    setDefinition({
                                                                        ...definition,
                                                                        options: newOptions,
                                                                    });
                                                                },
                                                            },
                                                            {
                                                                initialValue: option.label,
                                                                validate: (newLabel) => {
                                                                    if (newLabel.length === 0)
                                                                        return 'Label can not be empty';
                                                                    if (
                                                                        options.some(
                                                                            (opt) =>
                                                                                opt.label ===
                                                                                    newLabel &&
                                                                                opt.label !==
                                                                                    option.label
                                                                        )
                                                                    ) {
                                                                        return 'Another option already exists with this label';
                                                                    }
                                                                    return null;
                                                                },
                                                            }
                                                        );
                                                    },
                                                },
                                                {
                                                    key: 'deleteOption',
                                                    title: 'Delete',
                                                    icon: Icon.Trashcan,
                                                    trigger: () => {
                                                        void confirm({
                                                            title: 'Delete option',
                                                            description: `Are you sure you want to delete "${option.label}" and everywhere it is used in this block?`,
                                                            kind: 'danger',
                                                            confirmLabel: 'Delete',
                                                            onConfirm: () => {
                                                                const newOptions = [...options];
                                                                newOptions.splice(index, 1);
                                                                setDefinition({
                                                                    ...definition,
                                                                    options: newOptions,
                                                                });
                                                            },
                                                        });
                                                    },
                                                },
                                            ],
                                        },
                                    ],
                                });
                            },
                        }),
                    });
                },
            },
        ],
    });
}

/**
 * Cell content for a Select column.
 */
function RecordSelectValue(
    props: RecordValueRenderProps<string[], SelectColumnDefinition>
): React.ReactElement {
    const {
        value,
        selected,
        definition,
        onValueChange,
        definitionColorKind,
        readOnly,
        displayMode,
        onKeyDown,
        editor,
        nodeKey,
    } = props;
    const buttonRef = React.useRef<HTMLButtonElement>(null);

    const paletteHandle = usePaletteHandle(buttonRef, {
        onClose: () => {
            !isInModal && buttonRef.current?.focus();
        },
    });

    React.useEffect(() => {
        // If the current cell is `selected`, focus the + button
        selected ? buttonRef.current?.focus() : buttonRef.current?.blur();
    }, [selected]);

    const isInModal = displayMode === 'modal';
    // Only show the controls when this RecordValue is used in the modal. In cards + table, we show controls in a different way.
    const showControls = isInModal && !readOnly;

    /** Create a palette to select an option from the select definition */
    const openSelectValuePalette = () => {
        if (readOnly) return;

        paletteHandle.open(
            createSelectOptionsPalette({
                definition,
                value,
                nodeKey,
                editor,
                onValueChange,
            })
        );
    };

    const label =
        value.length > 0
            ? definition.multiple
                ? 'Edit options'
                : 'Edit option'
            : definition.multiple
              ? 'Add options'
              : 'Add option';

    return (
        <div className="relative flex flex-1">
            <div className="flex flex-1 flex-wrap gap-2">
                <React.Suspense fallback={null}>
                    {value.map((optionId) => {
                        const option = definition.options.find((op) => op.value === optionId);
                        if (!option) {
                            return null;
                        }
                        return (
                            <Tag
                                key={option.value}
                                deletable={!readOnly && definition.multiple}
                                kind={definitionColorKind ?? 'info'}
                                onPress={() =>
                                    definition.multiple
                                        ? onValueChange(value.filter((v) => v !== option.value))
                                        : null
                                }
                                size="small"
                            >
                                {option.label}
                            </Tag>
                        );
                    })}
                </React.Suspense>
            </div>
            {showControls ? (
                <InsertValueButton
                    buttonRef={buttonRef}
                    leadingIcon={value.length > 0 ? Icon.Pencil : Icon.Plus}
                    active={paletteHandle.opened}
                    label={label}
                    onKeyDown={onKeyDown}
                    onPress={openSelectValuePalette}
                    tooltip={{
                        label,
                        shortcut: 'space',
                    }}
                />
            ) : null}
        </div>
    );
}

type SearchToAddOptionCommandsParams = {
    /** The current column definition */
    definition: DeepReadonly<SelectColumnDefinition>;
    /** Callback to update the definition */
    setDefinition: (newDefinition: SelectColumnDefinition) => any;
    /** The user’s current search input */
    query: string;
    /** Called when user inserts a new option after typing in input, used to e.g. auto-insert the new option */
    onNewOptionAdded?: (newOption: SelectColumnOption) => void;
    /** Callback that returns a palette command for each existing option (so you can produce either a descendant command or a selection trigger). */
    optionCommandBuilder: (option: SelectColumnOption, index: number) => PaletteCommandDef;
};

/**
 * Creates commands for inline cell editing of a Select column.
 * Includes:
 * - Searching/filtering existing options
 * - Adding a new option
 */
function searchToAddOptionCommands({
    definition,
    setDefinition,
    query,
    onNewOptionAdded,
    optionCommandBuilder,
}: SearchToAddOptionCommandsParams): PaletteCommandDef[] {
    const optionCommands = definition.options.map((option, index) =>
        optionCommandBuilder(option, index)
    );

    let filteredCommands: PaletteCommandDef[] = [];

    if (query) {
        const matchedResults = matchCommands(query, optionCommands);
        filteredCommands = matchedResults.map((result) => result.item);
    } else {
        filteredCommands = optionCommands;
    }

    // Insert `Add "query"` command to the top if the user typed new value
    const alreadyExists = !!definition.options.find(
        (opt) => opt.label.toLowerCase() === query.toLowerCase()
    );

    if (query && !alreadyExists) {
        filteredCommands.unshift({
            key: 'add_new_option',
            title: `Add "${query}"`,
            icon: Icon.Plus,
            trigger: () => {
                const newOption: SelectColumnOption = {
                    value: generateKey(),
                    label: query,
                    color: 'blue',
                };
                const newOptions = [...definition.options, newOption];

                // Update the definition
                const result = setDefinition({
                    ...definition,
                    options: newOptions,
                });

                // If the parent wants to do something (like auto-select the new option), do it now
                onNewOptionAdded?.(newOption);

                return result;
            },
        });
    }

    return filteredCommands;
}
